import React, { useState, useEffect } from 'react';
import {
  adminInspectionService,
  adminInventoryService
} from '../../services/adminService';
import CreateCarModal  from './workflow/CreateCarModal';
import CarContentModal from './workflow/CarContentModal';
import PublishCarModal from './workflow/PublishCarModal';
import '../../styles/WorkflowStyles.css';

const WorkflowManagement = () => {
  const [inspections,        setInspections]        = useState([]);
  const [loading,            setLoading]            = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [modalType,          setModalType]          = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await adminInspectionService.getWorkflowInspections();
      const all = res.car_inspections || res.data || [];
      setInspections(all);
    } catch (err) {
      console.error('Workflow load error:', err);
      alert('Failed to load workflow data: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const openModal  = (type, insp) => { setModalType(type); setSelectedInspection(insp); };
  const closeModal = ()            => { setModalType(null); setSelectedInspection(null); };

  const stage1 = inspections.filter(i => {
    const hasPricing     = i.status === 'priced' || !!i.estimated_price;
    const noCar          = !i.car_id;
    const sellerAccepted = i.sell_request?.status === 'price_accepted';
    return hasPricing && noCar && sellerAccepted;
  });

  const stage2 = inspections.filter(i => {
    if (!i.car_id || !i.car) return false;
    return i.car.owned_by_platform === false;
  });

  const stage3 = inspections.filter(i => {
    if (!i.car_id || !i.car) return false;
    return i.car.owned_by_platform === true && i.car.content_verified === false;
  });

  const stage4 = inspections.filter(i => {
    if (!i.car_id || !i.car) return false;
    return i.car.content_verified === true && i.car.listing_status === 'draft';
  });

  const stage5 = inspections.filter(i => {
    if (!i.car_id || !i.car) return false;
    return i.car.listing_status === 'live';
  });

  if (loading) return (
    <div className="workflow-loading">
      <div className="spinner-large" />
      <p>Loading workflow data…</p>
    </div>
  );

  return (
    <div className="workflow-management-new">

      <div className="workflow-header">
        <div>
          <h1>🔄 Car Workflow Management</h1>
          <p>Create → Inventory → Content → Publish</p>
        </div>
        <button className="refresh-btn" onClick={loadData}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M21.5 2V8M21.5 8H15.5M21.5 8L18.5 5C17.2 3.7 15.4 2.8 13.5 2.3C9.5 1.3 5.5 2.9 3.5 6.3C1.5 9.7 1.8 14 4.5 17M2.5 22V16M2.5 16H8.5M2.5 16L5.5 19C6.8 20.3 8.6 21.2 10.5 21.7C14.5 22.7 18.5 21.1 20.5 17.7C22.5 14.3 22.2 10 19.5 7"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Refresh
        </button>
      </div>

      {inspections.length === 0 && !loading && (
        <div className="workflow-debug">
          <p>⚠️ No workflow items found. Check:</p>
          <ul>
            <li>Route <code>GET /api/v1/admin/car_inspections/workflow</code> is registered</li>
            <li>Inspections have status = <code>priced</code></li>
            <li>Sellers accepted price (<code>sell_request.status = 'price_accepted'</code>)</li>
          </ul>
        </div>
      )}

      {/* ── 4-Column Pipeline ── */}
      <div className="pipeline-grid">

        <PipelineColumn step="1" title="Create Car" icon="🚗"
          count={stage1.length} color="#28a745" description="Seller accepted price">
          {stage1.map(i => (
            <PipelineCard key={i.id} inspection={i} showPrice
              actionLabel="🚗 Create Car" actionClass="btn-pipeline btn-p-green"
              onAction={() => openModal('create', i)} />
          ))}
        </PipelineColumn>

        <div className="pipeline-arrow">›</div>

        <PipelineColumn step="2" title="Inventory" icon="📦"
          count={stage2.length} color="#003566" description="Prepare buyer listing">
          {stage2.map(i => (
            <PipelineCard key={i.id} inspection={i} showCarId
              actionLabel="📦 Move to Inventory" actionClass="btn-pipeline btn-p-blue"
              onAction={() => openModal('inventory', i)} />
          ))}
        </PipelineColumn>

        <div className="pipeline-arrow">›</div>

        <PipelineColumn step="3" title="Add Content" icon="📝"
          count={stage3.length} color="#6f42c1" description="Features & specs">
          {stage3.map(i => (
            <PipelineCard key={i.id} inspection={i} showCarId
              actionLabel="📝 Manage Content" actionClass="btn-pipeline btn-p-purple"
              onAction={() => openModal('content', i)} />
          ))}
        </PipelineColumn>

        <div className="pipeline-arrow">›</div>

        <PipelineColumn step="4" title="Publish" icon="🚀"
          count={stage4.length} color="#c9910a" description="Content verified">
          {stage4.map(i => (
            <PipelineCard key={i.id} inspection={i} showCarId
              actionLabel="🚀 Publish Car" actionClass="btn-pipeline btn-p-gold"
              onAction={() => openModal('publish', i)} />
          ))}
        </PipelineColumn>

      </div>

      {/* ── Published Table ── */}
      <div className="published-section">
        <div className="published-section-header">
          <div className="published-title-row">
            <h2>✅ Published Cars</h2>
            <span className="published-count">{stage5.length}</span>
          </div>
          <p>Live on platform — visible to buyers</p>
        </div>

        {stage5.length === 0 ? (
          <div className="published-empty"><p>No published cars yet</p></div>
        ) : (
          <div className="published-table-wrapper">
            <table className="published-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Booking Ref</th>
                  <th>Vehicle</th>
                  <th>Car ID</th>
                  <th>City</th>
                  <th>KM Driven</th>
                  <th>Price</th>
                  <th>Inventory</th>
                  <th>Content</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {stage5.map(i => (
                  <tr key={i.id}>
                    <td><span className="id-badge">#{i.id}</span></td>
                    <td><span className="booking-ref-table">{i.booking_reference || '—'}</span></td>
                    <td><span className="vehicle-name">{i.sell_request?.brand} {i.sell_request?.model} {i.sell_request?.year}</span></td>
                    <td><span className="car-id-pill">#{i.car_id}</span></td>
                    <td>{i.city || '—'}</td>
                    <td>{Number(i.sell_request?.km_driven || 0).toLocaleString()} km</td>
                    <td><span className="price-cell">₹{Number(i.estimated_price || 0).toLocaleString()}</span></td>
                    <td>
                      {i.car?.owned_by_platform
                        ? <span className="badge-yes">📦 Yes</span>
                        : <span className="badge-no">✗ No</span>}
                    </td>
                    <td>
                      {i.car?.content_verified
                        ? <span className="badge-yes">✅ Verified</span>
                        : <span className="badge-no">✗ Pending</span>}
                    </td>
                    <td><span className="live-badge">✅ Live</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {modalType && selectedInspection && (
        <>
          {modalType === 'create' && (
            <CreateCarModal inspection={selectedInspection} onClose={closeModal}
              onSuccess={() => { closeModal(); loadData(); }} />
          )}
          {modalType === 'inventory' && (
            <InventoryModal inspection={selectedInspection} onClose={closeModal}
              onSuccess={() => { closeModal(); loadData(); }} />
          )}
          {modalType === 'content' && (
            <CarContentModal inspection={selectedInspection} onClose={closeModal}
              onSuccess={() => { closeModal(); loadData(); }} />
          )}
          {modalType === 'publish' && (
            <PublishCarModal inspection={selectedInspection} onClose={closeModal}
              onSuccess={() => { closeModal(); loadData(); }} />
          )}
        </>
      )}
    </div>
  );
};

// -----------------------------------------------------------
// PipelineColumn
// -----------------------------------------------------------
const PipelineColumn = ({ step, title, icon, count, color, description, children }) => (
  <div className="pipeline-col">
    <div className="pipeline-col-header" style={{ borderTopColor: color }}>
      <div className="pipeline-col-meta">
        <span className="pipeline-step" style={{ background: color }}>Step {step}</span>
        <span className="pipeline-count" style={{ color }}>{count}</span>
      </div>
      <div className="pipeline-col-title">
        <span className="pipeline-col-icon">{icon}</span>
        <h3>{title}</h3>
      </div>
      <p className="pipeline-col-desc">{description}</p>
    </div>
    <div className="pipeline-col-body">
      {count === 0
        ? <div className="pipeline-empty"><p>No items</p></div>
        : children}
    </div>
  </div>
);

// -----------------------------------------------------------
// PipelineCard
// -----------------------------------------------------------
const PipelineCard = ({ inspection, showPrice, showCarId, actionLabel, actionClass, onAction }) => (
  <div className="pipeline-card">
    <div className="pipeline-card-top">
      <span className="id-badge">#{inspection.id}</span>
      <span className="pipeline-status-badge" style={{
        background: inspection.status === 'priced' ? '#28a74518' : '#ffc10718',
        color:      inspection.status === 'priced' ? '#28a745'   : '#c9910a',
      }}>
        {inspection.status}
      </span>
    </div>

    <div className="pipeline-card-vehicle">
      {inspection.sell_request?.brand} {inspection.sell_request?.model}
      <span className="pipeline-card-year"> {inspection.sell_request?.year}</span>
    </div>

    <div className="pipeline-card-meta">
      <span>📍 {inspection.city}</span>
      <span>🛣️ {Number(inspection.sell_request?.km_driven || 0).toLocaleString()} km</span>
    </div>

    {showPrice && inspection.estimated_price && (
      <div className="pipeline-card-price">
        ₹{Number(inspection.estimated_price).toLocaleString()}
      </div>
    )}

    {showCarId && inspection.car_id && (
      <div className="pipeline-card-carid">
        Car <span>#{inspection.car_id}</span>
      </div>
    )}

    <button className={actionClass} onClick={onAction}>{actionLabel}</button>
  </div>
);

// -----------------------------------------------------------
// InventoryModal
// -----------------------------------------------------------
const InventoryModal = ({ inspection, onClose, onSuccess }) => {
  const [processing, setProcessing] = useState(false);

  const handleMove = async () => {
    if (!window.confirm('Prepare buyer listing for this car?')) return;
    try {
      setProcessing(true);
      await adminInventoryService.moveToInventory(inspection.car_id);
      alert('✅ Buyer listing prepared! Car moved to content stage.');
      onSuccess();
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.message || err.message));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>📦 Prepare Buyer Listing</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p><strong>Car ID:</strong> #{inspection.car_id}</p>
          <p><strong>Vehicle:</strong> {inspection.sell_request?.brand} {inspection.sell_request?.model} {inspection.sell_request?.year}</p>
          <p><strong>Price:</strong> ₹{Number(inspection.estimated_price || 0).toLocaleString()}</p>
          <p style={{ marginTop: '12px', color: '#555', fontSize: '13px', lineHeight: '1.5' }}>
            This will prepare the buyer-facing listing from the inspection report
            and mark the car as platform inventory.
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn-modal-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-modal-submit" onClick={handleMove} disabled={processing}>
            {processing ? '⏳ Processing…' : '📦 Move to Inventory'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkflowManagement;